import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Button } from "./ui/button";
import { ArrowRight, Check } from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const services = [
  "Technology infrastructure consulting",
  "Systems architecture & deployment",
  "Apple ecosystem solutions",
  "Cybersecurity assessment",
  "Real estate technology integration",
  "Operational automation",
];

const targetClients = ["Enterprises", "Investors", "Growing companies", "Startups"];

export const Consulting = () => {
  const sectionRef = useRef(null);
  const bgRef = useRef(null);
  const headerRef = useRef(null);
  const contentRef = useRef(null);
  const servicesRef = useRef([]);
  const clientsRef = useRef([]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Parallax background
      gsap.to(bgRef.current, {
        yPercent: 30,
        ease: "none",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: true
        }
      });

      // Header animation
      const headerTl = gsap.timeline({
        scrollTrigger: {
          trigger: headerRef.current,
          start: "top 80%",
          toggleActions: "play none none reverse"
        }
      });

      headerTl.from(headerRef.current.querySelector('.section-number'), {
        y: 30,
        opacity: 0,
        duration: 0.5,
        ease: "power3.out"
      })
      .from(headerRef.current.querySelector('h2'), {
        y: 80,
        opacity: 0,
        skewY: 5,
        duration: 0.8,
        ease: "power3.out"
      }, "-=0.3")
      .from(headerRef.current.querySelector('p'), {
        y: 40,
        opacity: 0,
        duration: 0.6,
        ease: "power3.out"
      }, "-=0.4");

      // Services with stagger
      servicesRef.current.forEach((service, index) => {
        gsap.from(service, {
          x: -50,
          opacity: 0,
          duration: 0.5,
          ease: "power3.out",
          scrollTrigger: {
            trigger: service,
            start: "top 85%",
            toggleActions: "play none none reverse"
          },
          delay: index * 0.1
        });

        // Checkmark bounce
        const check = service.querySelector('.check-icon');
        gsap.from(check, {
          scale: 0,
          rotation: -90,
          duration: 0.4,
          ease: "back.out(2)",
          scrollTrigger: {
            trigger: service,
            start: "top 85%",
            toggleActions: "play none none reverse"
          },
          delay: 0.2 + index * 0.1
        });
      });

      // Clients tags
      clientsRef.current.forEach((client, index) => {
        gsap.from(client, {
          scale: 0,
          opacity: 0,
          duration: 0.4,
          ease: "back.out(2)",
          scrollTrigger: {
            trigger: client,
            start: "top 90%",
            toggleActions: "play none none reverse"
          },
          delay: index * 0.1
        });
      });

      // CTA button
      const cta = sectionRef.current.querySelector('.cta-button');
      gsap.from(cta, {
        y: 30,
        opacity: 0,
        duration: 0.6,
        ease: "power3.out",
        scrollTrigger: {
          trigger: cta,
          start: "top 90%",
          toggleActions: "play none none reverse"
        }
      });

    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const scrollToContact = () => {
    const element = document.querySelector("#contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      ref={sectionRef}
      id="consulting"
      data-testid="consulting-section"
      className="relative py-24 md:py-32 bg-obsidian overflow-hidden"
    >
      {/* Background with Parallax */}
      <div ref={bgRef} className="absolute inset-0 -top-20 -bottom-20">
        <img
          src="https://images.unsplash.com/photo-1765046255517-412341954c4c?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt=""
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-obsidian/95 to-obsidian" />
      </div>

      <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left Column */}
          <div ref={headerRef} className="lg:col-span-5">
            <span className="section-number text-xs uppercase tracking-[0.3em] text-concrete/60 font-medium block mb-4">
              05
            </span>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-concrete mb-6">
              Consulting & Advisory
            </h2>
            <p className="text-base md:text-lg text-concrete/70 leading-relaxed">
              Leveraging 20+ years of international technology operations experience 
              to help organizations design, deploy and scale their technical infrastructure.
            </p>
          </div>

          {/* Right Column */}
          <div ref={contentRef} className="lg:col-span-7 space-y-12">
            {/* Services */}
            <div>
              <h3 className="text-xs uppercase tracking-[0.3em] text-concrete/60 font-medium mb-6">
                Services
              </h3>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {services.map((service, index) => (
                  <li
                    key={service}
                    ref={(el) => servicesRef.current[index] = el}
                    className="flex items-center gap-3 text-concrete/90"
                  >
                    <Check className="check-icon w-4 h-4 text-steel flex-shrink-0" />
                    <span className="text-sm md:text-base">{service}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Target Clients */}
            <div>
              <h3 className="text-xs uppercase tracking-[0.3em] text-concrete/60 font-medium mb-6">
                Target Clients
              </h3>
              <div className="flex flex-wrap gap-3">
                {targetClients.map((client, index) => (
                  <span
                    key={client}
                    ref={(el) => clientsRef.current[index] = el}
                    className="px-4 py-2 border border-concrete/20 text-concrete/80 text-sm uppercase tracking-wider"
                  >
                    {client}
                  </span>
                ))}
              </div>
            </div>

            {/* CTA */}
            <Button
              onClick={scrollToContact}
              data-testid="consulting-cta"
              className="cta-button group bg-concrete text-obsidian hover:bg-concrete/90 px-8 py-6 text-sm uppercase tracking-widest font-medium"
            >
              Start a Conversation
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Consulting;
